import { inject } from '@angular/core';
import { HttpInterceptorFn, HttpRequest, HttpHandlerFn, HttpEvent } from '@angular/common/http';
import { Observable, EMPTY } from 'rxjs';
import { switchMap, catchError, tap } from 'rxjs/operators';
import { Router } from '@angular/router';

import { Oauth2Service } from '../services/oauth2.service';
import { TokenStorageService } from '../services/token-storage.service';
import { TokenRefreshService } from '../services/token-refresh.service';
import { routeNames } from '../../../shared/route-names';


export const tokenInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const oauth2Service = inject(Oauth2Service);
  const tokenStorage = inject(TokenStorageService);
  const tokenRefreshService = inject(TokenRefreshService);
  const router = inject(Router);

  if (shouldSkipAuth(req)) {
    return next(req);
  }

  const token = tokenStorage.getAccessToken();

  if (!token) {
    return next(req);
  }

  const authenticatedRequest = addAuthToken(req, token);

  if (!tokenStorage.isAccessTokenExpired()) {
    return next(authenticatedRequest);
  }

  return handleExpiredToken(req, next, oauth2Service, tokenStorage, tokenRefreshService, router);
};

function shouldSkipAuth(req: HttpRequest<unknown>): boolean {
  const skipAuthUrls = [
    '/oauth2/entraid/callback',
    '/oauth2/refresh',
    '/oauth2/token',
    '/oauth2/providers',
    '/locales/',
    '/assets/'
  ];
  
  return skipAuthUrls.some(url => req.url.includes(url));
}

function addAuthToken(req: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
  return req.clone({
    headers: req.headers.set('Authorization', `Bearer ${token}`)
  });
}

function handleExpiredToken(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  oauth2Service: Oauth2Service,
  tokenStorage: TokenStorageService,
  tokenRefreshService: TokenRefreshService,
  router: Router
): Observable<HttpEvent<unknown>> {
  if (req.url.includes('/oauth2/change-user')) {
    return next(addAuthToken(req, tokenStorage.getAccessToken()!));
  }

  if (tokenRefreshService.isRefreshInProgress()) {
    return tokenRefreshService.waitForRefresh().pipe(
      switchMap(newToken => newToken ? next(addAuthToken(req, newToken)) : handleAuthFailure(tokenStorage, router)),
      catchError(() => handleAuthFailure(tokenStorage, router))
    );
  }

  tokenRefreshService.startRefresh();

  return oauth2Service.refreshToken().pipe(
    tap(response => {
      tokenStorage.setAccessToken(response.accessToken);
      tokenRefreshService.completeRefresh(response.accessToken);
    }),
    switchMap(response => next(addAuthToken(req, response.accessToken))),
    catchError(() => {
      tokenRefreshService.failRefresh();
      return handleAuthFailure(tokenStorage, router);
    })
  );
}


function handleAuthFailure(tokenStorage: TokenStorageService, router: Router): Observable<never> {
  tokenStorage.clearToken();
  
  if (!window.location.href.includes(routeNames.auth)) {
    router.navigate([routeNames.auth]);
  }
  
  return EMPTY;
}
